import { type ApolloClient } from "@apollo/client";
import { type EventInput } from "@package/ui-gql/index-analytics";
import { type Post } from "@package/ui-gql/index-data-access";
import { type Store, type UnknownAction } from "redux";
import { WallEngineAnalytics } from "../engine/wall-engine-analytics";
import { WallEngineBackground } from "../engine/wall-engine-background";
import { WallEngineCache } from "../engine/wall-engine-cache";
import { WallEngineDevice } from "../engine/wall-engine-device";
import { WallEngineResting } from "../engine/wall-engine-resting";
import { WallEngineScreen } from "../engine/wall-engine-screen";
import { WallEngineServices } from "../engine/wall-engine-services";
import {
  type LayoutPostPosition,
  type ReduxWallState,
  sliceWallResetState,
  sliceWallSetCaching,
  sliceWallSetInitialization,
  sliceWallSetProfileScreen,
  sliceWallSetScreens,
  sliceWallSetServices,
  sliceWallSetType,
  sliceWallUpdateBackground,
  sliceWallUpdateDevice,
  sliceWallUpdateDeviceId,
  sliceWallUpdateLocation,
  sliceWallUpdateProfile,
  sliceWallUpdateResting,
  sliceWallUpdateScreen,
  sliceWallUpdateScreenMenuLocation,
} from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import { getMediaDownloadUrl } from "../utils/wall-utils-gql";
import { type WallProvider, type WallProviderEvents } from "./wall-provider";

export class WallProviderLocal
  extends EventEmitter<WallProviderEvents>
  implements WallProvider
{
  private engineAnalytics?: WallEngineAnalytics;
  private engineBackground?: WallEngineBackground;
  private engineCache?: WallEngineCache;
  private engineDevice?: WallEngineDevice;
  private engineResting?: WallEngineResting;
  private engineScreen?: WallEngineScreen;
  private engineServices?: WallEngineServices;
  private timeoutActivation?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly clientDataAccess: ApolloClient<object>,
    private readonly clientAnalytics: ApolloClient<object> | undefined,
    private readonly store: Store<ReduxWallState, UnknownAction>,
    private readonly location?: string,
    private readonly device?: string,
    private readonly profile?: string,
    private readonly cachingEnabled: boolean = true,
  ) {
    super();

    if (!this.location && !this.profile) {
      throw new Error("A location or profile is required");
    }

    console.log("[wall-provider-local] Initializing...");

    this.engineServices = new WallEngineServices(this.clientDataAccess);
    this.engineAnalytics = new WallEngineAnalytics(
      this.store,
      this.clientDataAccess,
      this.clientAnalytics,
      this.device,
    );
    this.engineBackground = new WallEngineBackground(
      this.clientDataAccess,
      this.location,
      this.profile,
    );
    this.engineCache = new WallEngineCache(
      this.clientDataAccess,
      this.location,
      this.device,
      this.profile,
    );
    this.engineDevice = new WallEngineDevice(
      this.clientDataAccess,
      this.location,
      this.profile,
      this.device,
    );
    this.engineResting = new WallEngineResting(
      this.clientDataAccess,
      this.location,
      this.profile,
    );
    this.engineScreen = new WallEngineScreen(
      this.clientDataAccess,
      this.location,
      this.profile,
    );

    this.engineAnalytics.on("cache-reload-requested", () => {
      this.engineCache?.activate();
    });
    this.engineBackground.on("background-updated", (update) => {
      this.store.dispatch(sliceWallUpdateBackground(update));
    });
    this.engineCache.on("cache-started", () => {
      this.store.dispatch(sliceWallSetCaching(true));
    });
    this.engineCache.on("cache-updated", () => {
      this.store.dispatch(sliceWallSetCaching(false));
      this.engineBackground?.activate();
      this.engineDevice?.activate();
      this.engineResting?.activate();
      this.engineScreen?.activate();
      this.engineServices?.activate();
    });
    this.engineDevice.on("device-updated", (update) => {
      if (update.device) {
        this.store.dispatch(sliceWallUpdateDevice(update.device));
      }
      if (update.deviceId) {
        this.store.dispatch(sliceWallUpdateDeviceId(update.deviceId));
      }
      if (update.location) {
        this.store.dispatch(sliceWallUpdateLocation(update.location));
      }
      if (update.profile) {
        this.store.dispatch(sliceWallUpdateProfile(update.profile));
      }
      if (update.screenMenuLocation) {
        this.store.dispatch(
          sliceWallUpdateScreenMenuLocation(update.screenMenuLocation),
        );
      }
      if (update.profileScreen) {
        this.store.dispatch(sliceWallSetProfileScreen(update.profileScreen));
      }
      if (update.type) {
        this.store.dispatch(sliceWallSetType(update.type));
      }
    });
    this.engineResting.on("resting-updated", (update) => {
      this.store.dispatch(sliceWallUpdateResting(update));
    });
    this.engineScreen.on("screens-updated", (update) => {
      this.store.dispatch(sliceWallSetScreens(update));
    });
    this.engineScreen.on("screen-updated", (update) => {
      this.store.dispatch(
        sliceWallUpdateScreen({
          id: update.id,
          screen: update,
        }),
      );
    });
    this.engineScreen.on("request-resting", (update) => {
      this.engineResting?.startMediaDirect(update);
    });
    this.engineScreen.on("profile-changed", (update) => {
      this.emit("profile-updated", update);
    });
    this.engineServices.on("services-updated", (update) => {
      this.store.dispatch(sliceWallSetServices(update));
    });

    this.timeoutActivation = setTimeout(() => {
      this.activate();
      this.store.dispatch(sliceWallSetInitialization("running"));
    });

    console.log("[wall-provider-local] Initialized");
  }

  public destroy(): void {
    console.log("[wall-provider-local] Destroying...");

    this.engineAnalytics?.destroy();
    this.engineAnalytics = undefined;

    this.engineBackground?.destroy();
    this.engineBackground = undefined;

    this.engineCache?.destroy();
    this.engineCache = undefined;

    this.engineDevice?.destroy();
    this.engineDevice = undefined;

    this.engineResting?.destroy();
    this.engineResting = undefined;

    this.engineScreen?.destroy();
    this.engineScreen = undefined;

    if (this.timeoutActivation) {
      clearTimeout(this.timeoutActivation);
      this.timeoutActivation = undefined;
    }

    console.log("[wall-provider-local] Destroyed");
  }

  public async fetchMediaUrl(media: string): Promise<string | void> {
    if (this.cachingEnabled) {
      return this.engineCache?.fetchMediaUrl(media);
    }

    return (await getMediaDownloadUrl(this.clientDataAccess, media))?.url;
  }

  public async activate(): Promise<void> {
    console.log("[wall-provider-local] Activating...");

    this.store.dispatch(sliceWallResetState());

    if (this.cachingEnabled) {
      await this.engineCache?.activate();
    }
    await this.engineAnalytics?.activate();
    await this.engineBackground?.activate();
    await this.engineDevice?.activate();
    await this.engineResting?.activate();
    await this.engineScreen?.activate();
    await this.engineServices?.activate();

    console.log("[wall-provider-local] Activated");
  }

  public onBackgroundMediaVideoCompleted(): void {
    this.engineBackground?.activate();
  }
  public onReset(): void {
    console.log("[wall-provider-local] Resetting...");

    this.store.dispatch(sliceWallResetState());

    this.engineBackground?.activate();
    this.engineDevice?.activate();
    this.engineResting?.activate();
    this.engineScreen?.activate();

    console.log("[wall-provider-local] Reset complete");
  }
  public onAnalyticsEvent(event: EventInput): void {
    this.engineAnalytics?.enqueue(event);
  }
  public onFlyoutClosed(screenId: string) {
    this.engineResting?.activate();
    this.engineScreen?.handleFlyoutClosed(screenId);
  }
  public onFlyoutInteracted(screenId: string, post: Post) {
    this.engineResting?.activate();
    this.engineScreen?.handleFlyoutInteracted(screenId, post);
  }
  public onFlyoutKeepAlive(screenId: string, post: Post) {
    this.engineResting?.activate();
    this.engineScreen?.handleFlyoutKeepAlive(screenId, post);
  }
  public onPostInteracted(screenId: string, position: LayoutPostPosition) {
    this.engineResting?.activate();
    this.engineScreen?.handlePostInteracted(screenId, position);
  }
  public onPostFlyoutDirectActivation(screenId: string, postId: string) {
    this.engineResting?.activate();
    this.engineScreen?.handlePostFlyoutDirectActivation(screenId, postId);
  }
  public onPostRequestChange(screenId: string) {
    this.engineScreen?.handlePostChange(screenId);
  }
  public onRestingMediaInteracted(): void {
    this.engineResting?.activate();
  }
  public onRestingMediaVideoCompleted(): void {
    this.engineResting?.activate();
  }
  public onScreenMenuOpened(screenId: string): void {
    this.engineResting?.disable();
    this.engineScreen?.handleScreenMenuOpened(screenId);
  }
  public onScreenMenuClosed(screenId: string): void {
    this.engineResting?.activate();
    this.engineScreen?.handleScreenMenuClosed(screenId);
  }
}
